import React, { useState, useEffect, useCallback } from 'react';

// Импортируем вынесенные компоненты
import MainMenu from './components/MainMenu';
import DepositScreen from './components/DepositScreen';
import WechatScreen from './components/WechatScreen';
import ProfileScreen from './components/ProfileScreen';
import SupportScreen from './components/SupportScreen';
import OrderConfirmationScreen from './components/OrderConfirmationScreen';

export default function ExchangeApp() {
  const [currentScreen, setCurrentScreen] = useState('main');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [amount, setAmount] = useState('');
  const [fullName, setFullName] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [wechatPaymentMethod, setWechatPaymentMethod] = useState('');
  const [wechatAmount, setWechatAmount] = useState('');
  const [wechatFullName, setWechatFullName] = useState('');
  const [wechatQrCode, setWechatQrCode] = useState(null);
  const [tg, setTg] = useState(null);
  const [userId, setUserId] = useState(null);
  const [telegramUsername, setTelegramUsername] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userPhotoUrl, setUserPhotoUrl] = useState(null);
  const [profileTab, setProfileTab] = useState('requests'); // requests, history, stats
  const [selectedBank, setSelectedBank] = useState('');
  const [wechatSelectedBank, setWechatSelectedBank] = useState('');
  const [usdtNetwork, setUsdtNetwork] = useState('');
  const [wechatUsdtNetwork, setWechatUsdtNetwork] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null); // CNY to RUB rate - загружается с API
  const [usdtToCnyRate, setUsdtToCnyRate] = useState(null); // USDT to CNY rate - загружается с API
  const [limits, setLimits] = useState({ minAmount: 500, maxAmount: 20000, currency: 'CNY' }); // Лимиты - загружаются с API

  // ВАЖНО: Замените на ваш реальный домен API
  const API_URL = 'https://api.kit8exchange.com'; // FastAPI сервер на порту 8000

  // Данные пользователя - загружаются только с API (без моковых данных)
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    totalAmountCNY: 0,
    totalAmountRUB: 0,
  });

  // Вспомогательная функция для безопасного парсинга JSON
  const safeParseJSON = async (response) => {
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ Сервер вернул не JSON (Content-Type:', contentType, ')');
      console.error('Первые 300 символов ответа:', text.substring(0, 300));
      throw new Error('Сервер вернул некорректный ответ (не JSON). Проверьте, что API запущен и доступен.');
    }
    return response.json();
  };

  // Функция создания сессии пользователя
  const createUserSession = useCallback(async (telegramApp, telegramUserId) => {
    if (!telegramApp || !telegramUserId) return null;
    
    try {
      const initData = telegramApp.initData;
      
      // Если нет initData (режим разработки), создаем моковые данные
      const requestData = {
        initData: initData || '',
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      };
      
      console.log('Создание сессии с данными:', { 
        hasInitData: !!initData,
        userId: telegramUserId 
      });
      
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(requestData)
      });
      
      // Проверяем статус ответа
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Ошибка создания сессии:', errorData);
        throw new Error(errorData.detail?.message || errorData.message || 'Ошибка создания сессии');
      }
      
      const data = await response.json();
      
      if (data.success && data.sessionToken) {
        // Сохраняем токен сессии в localStorage
        localStorage.setItem('sessionToken', data.sessionToken);
        localStorage.setItem('userId', String(data.userId));
        localStorage.setItem('sessionExpiry', data.expiresAt);
        console.log('✅ Сессия создана успешно для пользователя:', data.userId);
        return data.sessionToken;
      } else {
        throw new Error(data.message || 'Ошибка создания сессии');
      }
    } catch (error) {
      console.error('❌ Ошибка создания сессии:', error);
      if (tg) {
        tg.showAlert('Ошибка авторизации: ' + error.message);
      }
      return null;
    }
  }, [API_URL, tg]);

  // Проверка валидности сессии
  const checkSessionValidity = useCallback(async () => {
    const sessionToken = localStorage.getItem('sessionToken');
    const storedUserId = localStorage.getItem('userId');
    
    if (!sessionToken || !storedUserId) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          sessionToken: sessionToken,
          userId: parseInt(storedUserId)
        })
      });
      
      if (!response.ok) {
        console.error('Ошибка проверки сессии, статус:', response.status);
        return false;
      }
      
      const data = await response.json();
      
      if (!data.success || !data.valid) {
        // Сессия истекла, очищаем данные
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('sessionExpiry');
        console.log('⚠️ Сессия недействительна');
        return false;
      }
      
      console.log('✅ Сессия действительна');
      return true;
    } catch (error) {
      console.error('❌ Ошибка проверки сессии:', error);
      return false;
    }
  }, [API_URL]);

  // Функция загрузки курсов валют
  const fetchExchangeRates = useCallback(async () => {
    try {
      console.log('📊 Загрузка курсов валют...');
      
      const response = await fetch(`${API_URL}/api/rates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        }
      });
      
      if (!response.ok) {
        console.error('❌ Ошибка загрузки курсов, статус:', response.status);
        return;
      }
      
      const data = await response.json();
      
      if (data.rubToCny && data.usdtToCny) {
        setExchangeRate(data.rubToCny);
        setUsdtToCnyRate(data.usdtToCny);
        console.log('✅ Курсы обновлены:', {
          rubToCny: data.rubToCny,
          usdtToCny: data.usdtToCny,
          lastUpdated: data.lastUpdated
        });
      } else {
        console.error('❌ Некорректный формат данных курсов');
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки курсов:', error);
    }
  }, [API_URL]);

  // Функция загрузки лимитов
  const fetchLimits = useCallback(async () => {
    try {
      console.log('💰 Загрузка лимитов...');
      
      const response = await fetch(`${API_URL}/api/limits`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        }
      });
      
      if (!response.ok) {
        console.warn('⚠️ Ошибка загрузки лимитов, статус:', response.status, '- используем значения по умолчанию');
        return;
      }
      
      const data = await response.json();
      
      if (data.minAmount && data.maxAmount) {
        setLimits({
          minAmount: data.minAmount,
          maxAmount: data.maxAmount,
          currency: data.currency || 'CNY'
        });
        console.log('✅ Лимиты обновлены:', {
          minAmount: data.minAmount,
          maxAmount: data.maxAmount,
          currency: data.currency
        });
      } else {
        console.warn('⚠️ Некорректный формат данных лимитов - используем значения по умолчанию');
      }
    } catch (error) {
      console.warn('⚠️ Ошибка загрузки лимитов:', error, '- используем значения по умолчанию');
    }
  }, [API_URL]);

  // Функция для выполнения авторизованных запросов
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    const sessionToken = localStorage.getItem('sessionToken');
    const storedUserId = localStorage.getItem('userId');
    
    if (!sessionToken || !storedUserId) {
      throw new Error('Сессия не найдена. Пожалуйста, перезапустите приложение.');
    }
    
    const defaultHeaders = {
      'Authorization': `Bearer ${sessionToken}`,
      'X-User-ID': storedUserId,
      'ngrok-skip-browser-warning': 'true'
    };
    
    // Для FormData не устанавливаем Content-Type
    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }
    
    return fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    });
  }, []);

  // Загрузка фото профиля пользователя
  const loadUserPhoto = useCallback(async () => {
    try {
      console.log('📸 Загрузка фото профиля...');
      
      const response = await makeAuthenticatedRequest(`${API_URL}/api/user/photo`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.photoUrl) {
          setUserPhotoUrl(data.photoUrl);
          console.log('✅ Фото профиля загружено');
        }
      } else {
        console.log('⚠️ Фото профиля недоступно');
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки фото профиля:', error);
    }
  }, [API_URL, makeAuthenticatedRequest]);

  // Загрузка данных пользователя
  const loadUserData = useCallback(async () => {
    try {
      console.log('📥 Загрузка данных пользователя...');
      console.log('Session token:', localStorage.getItem('sessionToken') ? 'существует' : 'отсутствует');
      console.log('User ID:', localStorage.getItem('userId'));
      
      // Загружаем заявки
      const requestsResponse = await makeAuthenticatedRequest(`${API_URL}/api/user/requests`);
      console.log('Requests response status:', requestsResponse.status);
      
      if (requestsResponse.ok) {
        const requestsData = await requestsResponse.json();
        console.log('Requests data:', requestsData);
        
        if (requestsData.requests && Array.isArray(requestsData.requests)) {
          // Преобразуем формат данных из FastAPI
          console.log('📋 Первая заявка из API:', requestsData.requests[0]);
          const formattedRequests = requestsData.requests.map(req => {
            // Форматируем дату в читаемый вид
            let formattedDate = '';
            const dateField = req.created_at || req.createdAt || req.created || req.date;
            console.log('📅 Дата заявки:', { 
              created_at: req.created_at, 
              createdAt: req.createdAt, 
              dateField 
            });
            if (dateField) {
              try {
                const dateObj = new Date(dateField);
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                formattedDate = `${day}.${month}.${year}`;
                console.log('✅ Дата отформатирована:', formattedDate);
              } catch (e) {
                console.error('❌ Ошибка парсинга даты:', e);
                formattedDate = '';
              }
            } else {
              console.warn('⚠️ Поле даты не найдено в заявке');
            }
            
            const formattedReq = {
              id: req.order_id || req.orderId || req.id,
              type: req.type === 'alipay' ? 'Alipay' : 'WeChat',
              amount: req.amount,
              status: req.status,
              date: formattedDate,
              paymentMethod: req.payment_method || req.paymentMethod || '',
              bank: req.bank || '',
              network: req.network || '',
              fullName: req.full_name || req.fullName || ''
            };
            return formattedReq;
          });
          console.log('📋 Первая отформатированная заявка:', formattedRequests[0]);
          setRequests(formattedRequests);
          console.log('✅ Загружено заявок:', formattedRequests.length);
        }
      } else {
        const errorData = await requestsResponse.json().catch(() => ({}));
        console.error('❌ Ошибка загрузки заявок:', errorData);
      }
      
      // Загружаем историю
      const historyResponse = await makeAuthenticatedRequest(`${API_URL}/api/user/history`);
      console.log('History response status:', historyResponse.status);
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        if (historyData.history && Array.isArray(historyData.history)) {
          const formattedHistory = historyData.history.map(item => {
            // Форматируем дату в читаемый вид
            let formattedDate = '';
            const dateField = item.created_at || item.createdAt || item.created || item.date;
            console.log('📅 Дата истории:', { 
              created_at: item.created_at, 
              createdAt: item.createdAt, 
              dateField 
            });
            if (dateField) {
              try {
                const dateObj = new Date(dateField);
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                formattedDate = `${day}.${month}.${year}`;
                console.log('✅ Дата истории отформатирована:', formattedDate);
              } catch (e) {
                console.error('❌ Ошибка парсинга даты истории:', e);
                formattedDate = '';
              }
            } else {
              console.warn('⚠️ Поле даты не найдено в истории');
            }
            
            return {
              id: item.order_id || item.orderId || item.id,
              type: item.type === 'alipay' ? 'Alipay' : 'WeChat',
              amount: item.amount,
              date: formattedDate,
              paymentMethod: item.payment_method || item.paymentMethod,
              rub: (item.payment_method || item.paymentMethod) === 'RUB' && exchangeRate ? item.amount * exchangeRate : 0
            };
          });
          setHistory(formattedHistory);
          console.log('✅ Загружена история:', formattedHistory.length);
        }
      } else {
        const errorData = await historyResponse.json().catch(() => ({}));
        console.error('❌ Ошибка загрузки истории:', errorData);
      }
      
      // Загружаем статистику
      const statsResponse = await makeAuthenticatedRequest(`${API_URL}/api/user/stats`);
      console.log('Stats response status:', statsResponse.status);
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData) {
          setStats({
            totalRequests: statsData.totalRequests || 0,
            totalAmountCNY: statsData.totalAmountCNY || 0,
            totalAmountRUB: statsData.totalAmountRUB || 0,
            completedRequests: statsData.completedRequests || 0,
            pendingRequests: statsData.pendingRequests || 0
          });
          console.log('✅ Загружена статистика');
        }
      } else {
        const errorData = await statsResponse.json().catch(() => ({}));
        console.error('❌ Ошибка загрузки статистики:', errorData);
      }
      
    } catch (error) {
      console.error('❌ Ошибка загрузки данных:', error);
      console.error('Error stack:', error.stack);
    }
  }, [API_URL, exchangeRate, makeAuthenticatedRequest]);

  // Инициализация Telegram Web App
  useEffect(() => {
    console.log('=== Инициализация Telegram WebApp ===');
    
    // СНАЧАЛА проверяем наличие Telegram WebApp
    if (window.Telegram?.WebApp) {
      const telegram = window.Telegram.WebApp;
      
      console.log('✅ Telegram WebApp найден');
      console.log('Platform:', telegram.platform);
      console.log('Version:', telegram.version);
      
      // Инициализируем Telegram WebApp
      telegram.ready();
      telegram.expand();
      telegram.enableClosingConfirmation();
      
      // Получаем данные
      const initData = telegram.initData;
      const user = telegram.initDataUnsafe?.user;
      
      console.log('initData length:', initData?.length);
      console.log('User:', user);
      
      if (!initData) {
        console.error('⚠️ КРИТИЧНО: initData пустой!');
        console.log('Это означает, что приложение открыто НЕ через Telegram');
        telegram.showAlert('Пожалуйста, откройте приложение через бота в Telegram');
        return;
      }
      
      if (!user?.id) {
        console.error('⚠️ КРИТИЧНО: User ID не найден!');
        telegram.showAlert('Ошибка получения данных пользователя');
        return;
      }
      
      // Устанавливаем данные
      setTg(telegram);
      setUserId(user.id);
      setTelegramUsername(user.username || '');
      setUserFirstName(user.first_name || '');
      setUserLastName(user.last_name || '');
      
      // Применяем тему
      document.body.style.backgroundColor = telegram.backgroundColor;
      
      console.log('✅ Инициализация завершена');
      console.log('User ID:', user.id);
      console.log('Username:', user.username);
      console.log('Name:', user.first_name, user.last_name);
      
    } else {
      // Telegram WebApp НЕ доступен - это означает открытие в обычном браузере
      console.error('❌ Telegram WebApp недоступен');
      console.log('window.Telegram:', window.Telegram);
      
      // ДЛЯ РАЗРАБОТКИ: создаем mock только если явно включен dev режим
      const isDevelopment = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        console.log('🔧 DEV MODE: создаем mock объект для локальной разработки');
        
        const mockTelegram = {
          ready: () => console.log('Mock: ready'),
          expand: () => console.log('Mock: expand'),
          enableClosingConfirmation: () => {},
          BackButton: {
            show: () => console.log('Mock: BackButton.show'),
            hide: () => console.log('Mock: BackButton.hide'),
            onClick: (fn) => console.log('Mock: BackButton.onClick'),
            offClick: (fn) => console.log('Mock: BackButton.offClick')
          },
          // ВАЖНО: в dev режиме тоже ставим пустой initData
          initData: '',
          initDataUnsafe: { 
            user: { 
              id: 123456789, 
              first_name: 'Test', 
              last_name: 'User',
              username: 'testuser'
            } 
          },
          backgroundColor: '#ffffff',
          showAlert: (msg) => alert(msg),
          close: () => console.log('Mock: close'),
          platform: 'unknown',
          version: '6.0'
        };
        
        setTg(mockTelegram);
        setUserId(123456789);
        setTelegramUsername('testuser');
        setUserFirstName('Test');
        setUserLastName('User');
        
        alert('⚠️ DEV MODE: Используются моковые данные. В продакшене откройте через Telegram!');
      } else {
        // В продакшене показываем ошибку
        document.body.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; text-align: center; font-family: sans-serif;">
            <div>
              <h1 style="color: #ef4444; margin-bottom: 16px;">⚠️ Ошибка</h1>
              <p style="color: #666; margin-bottom: 24px;">
                Это приложение должно открываться через Telegram Bot
              </p>
              <p style="color: #999; font-size: 14px;">
                Пожалуйста, откройте приложение через бота в Telegram
              </p>
            </div>
          </div>
        `;
      }
    }
  }, []);

  // Загрузка курсов валют и лимитов при открытии приложения
  useEffect(() => {
    fetchExchangeRates();
    fetchLimits();
    
    // Обновляем курсы каждые 5 минут
    const ratesInterval = setInterval(() => {
      fetchExchangeRates();
    }, 5 * 60 * 1000); // 5 минут
    
    // Обновляем лимиты каждые 30 минут
    const limitsInterval = setInterval(() => {
      fetchLimits();
    }, 30 * 60 * 1000); // 30 минут
    
    return () => {
      clearInterval(ratesInterval);
      clearInterval(limitsInterval);
    };
  }, [fetchExchangeRates, fetchLimits]);

  // Создание сессии после инициализации Telegram
  useEffect(() => {
    if (tg && userId) {
      // Проверяем, есть ли уже валидная сессия
      const existingToken = localStorage.getItem('sessionToken');
      const existingUserId = localStorage.getItem('userId');
      
      if (existingToken && existingUserId === String(userId)) {
        // Проверяем валидность существующей сессии
        checkSessionValidity().then(isValid => {
          if (isValid) {
            console.log('Сессия действительна, загружаем данные');
            loadUserData();
          } else {
            // Создаем новую сессию
            console.log('Создание новой сессии');
            createUserSession(tg, userId).then(sessionToken => {
              if (sessionToken) {
                console.log('Сессия создана успешно');
                loadUserData();
              } else {
                if (tg) {
                  tg.showAlert('Ошибка авторизации. Попробуйте перезапустить приложение.');
                }
              }
            });
          }
        });
      } else {
        // Создаем новую сессию
        console.log('Создание новой сессии');
        createUserSession(tg, userId).then(sessionToken => {
          if (sessionToken) {
            console.log('Сессия создана успешно');
            loadUserData();
          } else {
            if (tg) {
              tg.showAlert('Ошибка авторизации. Попробуйте перезапустить приложение.');
            }
          }
        });
      }

      // Автоматическая проверка сессии каждые 30 минут
      const sessionCheckInterval = setInterval(async () => {
        const isValid = await checkSessionValidity();
        if (!isValid && tg) {
          tg.showAlert('Сессия истекла. Приложение будет перезапущено.');
          setTimeout(() => {
            tg.close();
          }, 2000);
        }
      }, 30 * 60 * 1000); // 30 минут

      return () => clearInterval(sessionCheckInterval);
    }
  }, [tg, userId, createUserSession, checkSessionValidity, loadUserData]);

  // Управление кнопкой "Назад" Telegram
  useEffect(() => {
    if (tg && tg.BackButton) {
      if (currentScreen !== 'main') {
        // Показываем кнопку "Назад" на всех экранах кроме главного
        tg.BackButton.show();
        
        // Обработчик клика
        const handleBackClick = () => {
          setCurrentScreen('main');
        };
        
        tg.BackButton.onClick(handleBackClick);
        
        // Очистка при размонтировании или смене экрана
        return () => {
          tg.BackButton.offClick(handleBackClick);
          if (currentScreen === 'main') {
            tg.BackButton.hide();
          }
        };
      } else {
        // На главном экране скрываем кнопку
        tg.BackButton.hide();
      }
    }
  }, [tg, currentScreen]);

  // Автоподгрузка данных при открытии профиля
  useEffect(() => {
    if (currentScreen === 'profile' && userId) {
      const sessionToken = localStorage.getItem('sessionToken');
      if (sessionToken) {
        console.log('📥 Автоподгрузка данных профиля...');
        loadUserData();
        loadUserPhoto();
      } else {
        console.warn('⚠️ Сессия не найдена, пропускаем загрузку данных');
      }
    }
  }, [currentScreen, userId, loadUserData, loadUserPhoto]);

  const handleAlipayDeposit = async () => {
    // Проверка лимитов суммы
    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue < limits.minAmount) {
      if (tg) {
        tg.showAlert(`Минимальная сумма для пополнения: ${limits.minAmount.toLocaleString()} ${limits.currency}`);
      } else {
        alert(`Минимальная сумма для пополнения: ${limits.minAmount.toLocaleString()} ${limits.currency}`);
      }
      return;
    }
    
    if (amountValue > limits.maxAmount) {
      if (tg) {
        tg.showAlert(`Максимальная сумма для пополнения: ${limits.maxAmount.toLocaleString()} ${limits.currency}`);
      } else {
        alert(`Максимальная сумма для пополнения: ${limits.maxAmount.toLocaleString()} ${limits.currency}`);
      }
      return;
    }
    
    const isRubValid = paymentMethod === 'RUB' && selectedBank;
    const isUsdtValid = paymentMethod === 'USDT' && usdtNetwork;
    
    if (!paymentMethod || !amount || !fullName || !qrCode || (!isRubValid && !isUsdtValid)) {
      if (tg) {
        tg.showAlert('Пожалуйста, заполните все поля и загрузите QR код');
      } else {
        alert('Пожалуйста, заполните все поля и загрузите QR код');
      }
      return;
    }

    try {
      console.log('📤 Создание заявки Alipay...');
      
      // Создаем FormData для отправки файла
      const formData = new FormData();
      formData.append('paymentMethod', paymentMethod);
      formData.append('amount', parseFloat(amount));
      formData.append('fullName', fullName);
      formData.append('qrCode', qrCode);
      formData.append('type', 'alipay');
      if (selectedBank) formData.append('bank', selectedBank);
      if (usdtNetwork) formData.append('network', usdtNetwork);
      if (telegramUsername) formData.append('telegramUsername', telegramUsername);

      // Отправляем авторизованный запрос
      const response = await makeAuthenticatedRequest(`${API_URL}/api/deposit`, {
        method: 'POST',
        body: formData
      });
      
      console.log('Response status:', response.status);
      console.log('Response content-type:', response.headers.get('content-type'));
      
      // Проверяем, что ответ - это JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Сервер вернул не JSON:', text.substring(0, 200));
        throw new Error('Сервер вернул некорректный ответ. Проверьте, что API запущен.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || errorData.message || 'Ошибка создания заявки');
      }
      
      const data = await response.json();
      console.log('✅ ALIPAY Response data ПОЛНОСТЬЮ:', JSON.stringify(data, null, 2));
      console.log('data.success:', data.success);
      console.log('data.orderId:', data.orderId);
      console.log('data.order_id:', data.order_id);
      console.log('Все ключи ответа:', Object.keys(data));
      
      // Поддерживаем оба варианта: orderId и order_id
      const actualOrderId = data.orderId || data.order_id;
      console.log('🔍 actualOrderId результат:', actualOrderId);
      console.log('🔍 Условие выполнится?', (!!data.success && !!actualOrderId));
      
      if (data.success && actualOrderId) {
        console.log('✅ ✅ ✅ Заявка Alipay создана:', actualOrderId);
        
        // Сохраняем детали заявки и переходим на страницу подтверждения
        const orderData = {
          orderId: actualOrderId,
          type: 'Alipay',
          amount: amount,
          paymentMethod: paymentMethod,
          bank: selectedBank,
          network: usdtNetwork,
          fullName: fullName,
          paymentDetails: data.paymentDetails || data.payment_details,
          status: data.status || 'processing'
        };
        console.log('📦 📦 📦 Сохраняем orderDetails:', JSON.stringify(orderData, null, 2));
        console.log('📦 Проверка: orderData.orderId =', orderData.orderId);
        setOrderDetails(orderData);
        
        console.log('🔄 Переход на orderConfirmation');
        setCurrentScreen('orderConfirmation');
        
        // Очищаем форму
        setPaymentMethod('');
        setAmount('');
        setFullName('');
        setQrCode(null);
        setSelectedBank('');
        setUsdtNetwork('');
        
        // Перезагружаем данные пользователя
        loadUserData();
      } else {
        console.error('❌ ❌ ❌ Условие не выполнилось!');
        console.error('data.success =', data.success);
        console.error('actualOrderId =', actualOrderId);
        throw new Error(data.message || 'Ошибка создания заявки: orderId не получен');
      }
      
    } catch (error) {
      console.error('Error:', error);
      if (tg) {
        tg.showAlert('Ошибка при создании заявки. Попробуйте позже.');
      } else {
        alert('Ошибка при создании заявки. Попробуйте позже.');
      }
    }
  };

  const handleWechatDeposit = async () => {
    // Проверка лимитов суммы
    const amountValue = parseFloat(wechatAmount);
    if (isNaN(amountValue) || amountValue < limits.minAmount) {
      if (tg) {
        tg.showAlert(`Минимальная сумма для пополнения: ${limits.minAmount.toLocaleString()} ${limits.currency}`);
      } else {
        alert(`Минимальная сумма для пополнения: ${limits.minAmount.toLocaleString()} ${limits.currency}`);
      }
      return;
    }
    
    if (amountValue > limits.maxAmount) {
      if (tg) {
        tg.showAlert(`Максимальная сумма для пополнения: ${limits.maxAmount.toLocaleString()} ${limits.currency}`);
      } else {
        alert(`Максимальная сумма для пополнения: ${limits.maxAmount.toLocaleString()} ${limits.currency}`);
      }
      return;
    }
    
    const isRubValid = wechatPaymentMethod === 'RUB' && wechatSelectedBank;
    const isUsdtValid = wechatPaymentMethod === 'USDT' && wechatUsdtNetwork;
    
    if (!wechatPaymentMethod || !wechatAmount || !wechatFullName || !wechatQrCode || (!isRubValid && !isUsdtValid)) {
      if (tg) {
        tg.showAlert('Пожалуйста, заполните все поля и загрузите QR код');
      } else {
        alert('Пожалуйста, заполните все поля и загрузите QR код');
      }
      return;
    }

    try {
      console.log('📤 Создание заявки WeChat...');
      
      // Создаем FormData для отправки файла
      const formData = new FormData();
      formData.append('paymentMethod', wechatPaymentMethod);
      formData.append('amount', parseFloat(wechatAmount));
      formData.append('fullName', wechatFullName);
      formData.append('qrCode', wechatQrCode);
      formData.append('type', 'wechat');
      if (wechatSelectedBank) formData.append('bank', wechatSelectedBank);
      if (wechatUsdtNetwork) formData.append('network', wechatUsdtNetwork);
      if (telegramUsername) formData.append('telegramUsername', telegramUsername);

      // Отправляем авторизованный запрос
      const response = await makeAuthenticatedRequest(`${API_URL}/api/deposit`, {
        method: 'POST',
        body: formData
      });
      
      console.log('Response status:', response.status);
      console.log('Response content-type:', response.headers.get('content-type'));
      
      // Проверяем, что ответ - это JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Сервер вернул не JSON:', text.substring(0, 200));
        throw new Error('Сервер вернул некорректный ответ. Проверьте, что API запущен.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || errorData.message || 'Ошибка создания заявки');
      }
      
      const data = await response.json();
      console.log('✅ WECHAT Response data ПОЛНОСТЬЮ:', JSON.stringify(data, null, 2));
      console.log('data.success:', data.success);
      console.log('data.orderId:', data.orderId);
      console.log('data.order_id:', data.order_id);
      console.log('Все ключи ответа:', Object.keys(data));
      
      // Поддерживаем оба варианта: orderId и order_id
      const actualOrderId = data.orderId || data.order_id;
      console.log('🔍 actualOrderId результат:', actualOrderId);
      console.log('🔍 Условие выполнится?', (!!data.success && !!actualOrderId));
      
      if (data.success && actualOrderId) {
        console.log('✅ ✅ ✅ Заявка WeChat создана:', actualOrderId);
        
        // Сохраняем детали заявки и переходим на страницу подтверждения
        const orderData = {
          orderId: actualOrderId,
          type: 'WeChat',
          amount: wechatAmount,
          paymentMethod: wechatPaymentMethod,
          bank: wechatSelectedBank,
          network: wechatUsdtNetwork,
          fullName: wechatFullName,
          paymentDetails: data.paymentDetails || data.payment_details,
          status: data.status || 'processing'
        };
        console.log('📦 📦 📦 Сохраняем orderDetails:', JSON.stringify(orderData, null, 2));
        console.log('📦 Проверка: orderData.orderId =', orderData.orderId);
        setOrderDetails(orderData);
        
        console.log('🔄 Переход на orderConfirmation');
        setCurrentScreen('orderConfirmation');
        
        // Очищаем форму
        setWechatPaymentMethod('');
        setWechatAmount('');
        setWechatFullName('');
        setWechatQrCode(null);
        setWechatSelectedBank('');
        setWechatUsdtNetwork('');
        
        // Перезагружаем данные пользователя
        loadUserData();
      } else {
        console.error('❌ ❌ ❌ WeChat: Условие не выполнилось!');
        console.error('data.success =', data.success);
        console.error('actualOrderId =', actualOrderId);
        throw new Error(data.message || 'Ошибка создания заявки: orderId не получен');
      }
      
    } catch (error) {
      console.error('❌ Error создания WeChat заявки:', error);
      if (tg) {
        tg.showAlert('Ошибка при создании заявки. Попробуйте позже.');
      } else {
        alert('Ошибка при создании заявки. Попробуйте позже.');
      }
    }
  };

  // Компоненты вынесены в отдельные файлы в папке components/
  // Отправка реквизитов происходит через Telegram бота (см. telegram_bot_polling.py)

  return (
    <div className="font-sans">
      {currentScreen === 'main' && (
        <MainMenu 
          setCurrentScreen={setCurrentScreen}
          exchangeRate={exchangeRate}
          usdtToCnyRate={usdtToCnyRate}
        />
      )}
      {currentScreen === 'profile' && (
        <ProfileScreen
          profileTab={profileTab}
          setProfileTab={setProfileTab}
          requests={requests}
          history={history}
          stats={stats}
          loadUserData={loadUserData}
          tg={tg}
          setOrderDetails={setOrderDetails}
          setCurrentScreen={setCurrentScreen}
          userFirstName={userFirstName}
          userLastName={userLastName}
          telegramUsername={telegramUsername}
          userPhotoUrl={userPhotoUrl}
        />
      )}
      {currentScreen === 'deposit' && (
        <DepositScreen
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          amount={amount}
          setAmount={setAmount}
          fullName={fullName}
          setFullName={setFullName}
          qrCode={qrCode}
          setQrCode={setQrCode}
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          usdtNetwork={usdtNetwork}
          setUsdtNetwork={setUsdtNetwork}
          exchangeRate={exchangeRate}
          usdtToCnyRate={usdtToCnyRate}
          handleAlipayDeposit={handleAlipayDeposit}
          telegramUsername={telegramUsername}
          limits={limits}
        />
      )}
      {currentScreen === 'wechat' && (
        <WechatScreen
          wechatPaymentMethod={wechatPaymentMethod}
          setWechatPaymentMethod={setWechatPaymentMethod}
          wechatAmount={wechatAmount}
          setWechatAmount={setWechatAmount}
          wechatFullName={wechatFullName}
          setWechatFullName={setWechatFullName}
          wechatQrCode={wechatQrCode}
          setWechatQrCode={setWechatQrCode}
          wechatSelectedBank={wechatSelectedBank}
          setWechatSelectedBank={setWechatSelectedBank}
          wechatUsdtNetwork={wechatUsdtNetwork}
          setWechatUsdtNetwork={setWechatUsdtNetwork}
          exchangeRate={exchangeRate}
          usdtToCnyRate={usdtToCnyRate}
          handleWechatDeposit={handleWechatDeposit}
          telegramUsername={telegramUsername}
          limits={limits}
        />
      )}
      {currentScreen === 'support' && <SupportScreen />}
      {currentScreen === 'orderConfirmation' && (
        <OrderConfirmationScreen
          orderDetails={orderDetails}
          usdtToCnyRate={usdtToCnyRate}
          tg={tg}
          setCurrentScreen={setCurrentScreen}
          setOrderDetails={setOrderDetails}
          apiUrl={API_URL}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
        />
      )}
    </div>
  );
}
